/**
 * Google Analytics 4 (gtag.js) — env-driven, SPA-aware bootstrap.
 *
 * Loads gtag.js and calls gtag('config', <id>) with `send_page_view: false`
 * so we control page_view firing ourselves. This is a single-page app, so the
 * default automatic page_view (which only fires on the initial document load)
 * would miss every client-side route change. Instead we fire page_view manually
 * on each navigation via `trackGaPageView()` (see ScrollToTop.tsx).
 *
 * The Measurement ID comes from `VITE_GA_MEASUREMENT_ID`. No-op when unset.
 */

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

let initialized = false;

function getMeasurementId(): string | undefined {
    return import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
}

export function initGoogleAnalytics(): void {
    if (initialized) return;
    // SSR / prerender safe — only run in a real browser.
    if (typeof window === "undefined" || typeof document === "undefined")
        return;

    const measurementId = getMeasurementId();
    if (!measurementId) {
        if (import.meta.env.DEV) {
            console.warn(
                "[googleAnalytics] VITE_GA_MEASUREMENT_ID not set — GA disabled.",
            );
        }
        return;
    }

    // Inject the gtag.js loader script.
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    // gtag must push the raw `arguments` object — do not refactor to (...args).
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
    };

    window.gtag("js", new Date());
    // Disable automatic page_view; we fire it manually on each route change.
    window.gtag("config", measurementId, { send_page_view: false });

    initialized = true;
}

/** Fire a GA4 page_view. Call on every route change. */
export function trackGaPageView(path?: string): void {
    if (typeof window === "undefined" || !window.gtag) return;
    const measurementId = getMeasurementId();
    if (!measurementId) return;

    window.gtag("event", "page_view", {
        page_path: path ?? window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
    });
}

/** Fire an arbitrary GA4 event. No-op when GA is unset. Never throws. */
export function trackGaEvent(
    eventName: string,
    params: Record<string, unknown> = {},
): void {
    if (typeof window === "undefined" || !window.gtag) return;
    if (!getMeasurementId()) return;
    try {
        window.gtag("event", eventName, params);
    } catch {
        // analytics must never break the UI
    }
}
